import React from "react";
import { useStateContext } from "../contexts/ContextProvider";

const Pagination = ({
  currentPage,
  totalPages,
  onPageChange,
  totalRecords,
  backendLimit,
  recordsCount,
  showRecordsInfo = true,
}) => {
  const { currentMode } = useStateContext();

  const getPageButtonClassName = (page) => {
    if (currentPage === page) {
      return "bg-blue-600 text-white";
    }
    if (currentMode === "Dark") {
      return "text-gray-300 bg-secondary-dark-bg-light border border-gray-600 hover:bg-gray-700";
    }
    return "text-gray-500 bg-white border border-gray-300 hover:bg-gray-50";
  };

  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      onPageChange(pageNumber);
    }
  };

  // Calculate the range of pages to show
  const getVisiblePages = () => {
    const delta = 2; // Number of pages to show on each side of current page
    const range = [];
    const rangeWithDots = [];

    for (
      let i = Math.max(2, currentPage - delta);
      i <= Math.min(totalPages - 1, currentPage + delta);
      i += 1
    ) {
      range.push(i);
    }

    if (currentPage - delta > 2) {
      rangeWithDots.push(1, "...");
    } else {
      rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (currentPage + delta < totalPages - 1) {
      rangeWithDots.push("...", totalPages);
    } else if (totalPages > 1) {
      rangeWithDots.push(totalPages);
    }

    return rangeWithDots;
  };

  if (totalPages <= 1) {
    return null;
  }

  const visiblePages = getVisiblePages();

  return (
    <div className="flex items-center justify-between mt-6">
      <div className="flex items-center space-x-2">
        {/* Previous Button */}
        <button
          type="button"
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className={`px-3 py-2 text-sm font-medium rounded-md disabled:opacity-50 disabled:cursor-not-allowed ${
            currentMode === "Dark"
              ? "text-gray-300 bg-secondary-dark-bg-light border border-gray-600 hover:bg-gray-700"
              : "text-gray-500 bg-white border border-gray-300 hover:bg-gray-50"
          }`}
        >
          Previous
        </button>

        {/* Page Numbers */}
        <div className="flex space-x-1">
          {visiblePages.map((page, index) => (
            <React.Fragment key={index}>
              {page === "..." ? (
                <span
                  className={`px-3 py-2 text-sm ${
                    currentMode === "Dark" ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  ...
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => handlePageChange(page)}
                  className={`px-3 py-2 text-sm font-medium rounded-md ${getPageButtonClassName(
                    page
                  )}`}
                >
                  {page}
                </button>
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Next Button */}
        <button
          type="button"
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className={`px-3 py-2 text-sm font-medium rounded-md disabled:opacity-50 disabled:cursor-not-allowed ${
            currentMode === "Dark"
              ? "text-gray-300 bg-secondary-dark-bg-light border border-gray-600 hover:bg-gray-700"
              : "text-gray-500 bg-white border border-gray-300 hover:bg-gray-50"
          }`}
        >
          Next
        </button>
      </div>

      {/* Page Info */}
      <div
        className={`text-sm ${
          currentMode === "Dark" ? "text-gray-300" : "text-gray-700"
        }`}
      >
        {showRecordsInfo && recordsCount > 0 && (
          <span className="mr-4">
            Showing {recordsCount === 0 ? 0 : (currentPage - 1) * backendLimit + 1} to{" "}
            {Math.min(currentPage * backendLimit, totalRecords)} of {totalRecords} records
          </span>
        )}
        Page {currentPage} of {totalPages}
      </div>
    </div>
  );
};

export default Pagination; 